<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  createExportLink,
  getExportLinks,
  revokeExportLink,
  revokeExportLinks,
  type ExportLinks,
  type UserSettings,
} from '@/api'
import { useToast } from '@/composables/useToast'
import { reportError } from '@/errorReport'
import { currentLocale } from '@/i18n'
import { useTodosStore } from '@/stores/todos'
import { confirmDialog, downloadFileOrOpen } from '@/telegramWebApp'

const props = defineProps<{ validSince: string | null }>()
const emit = defineEmits<{ revoked: [settings: UserSettings] }>()

const { t } = useI18n()
const store = useTodosStore()
const toast = useToast()

const links = reactive<Record<number, ExportLinks>>({})
const labels = reactive<Record<number, string>>({})
const busy = reactive<Record<number, boolean>>({})
const revoking = ref(false)

function formatDate(value: string): string {
  return new Date(value).toLocaleString(currentLocale(), { dateStyle: 'medium', timeStyle: 'short' })
}

const validSinceText = computed(() => (props.validSince ? formatDate(props.validSince) : ''))

// Fetched up front (cheap, no CalDAV call): the download button has to open the link right away,
// Telegram only allows that in direct response to the tap.
async function loadLinks(): Promise<void> {
  try {
    if (!store.calendarsLoaded) await store.loadCalendars()
    await Promise.all(
      store.calendars.map(async (calendar) => {
        links[calendar.id] = await getExportLinks(calendar.id)
      }),
    )
  } catch (e) {
    reportError(e, 'settings.export.err.load')
    toast.error(e, t('settings.export.err.load'))
  }
}

function download(calendarId: number): void {
  const entry = links[calendarId]
  if (entry) downloadFileOrOpen(entry.download_url, entry.file_name)
}

async function copy(url: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(url)
    toast.info(t('settings.export.copied'))
  } catch {
    // clipboard unavailable -- the link is still shown to select by hand
  }
}

async function addLink(calendarId: number): Promise<void> {
  busy[calendarId] = true
  try {
    const label = (labels[calendarId] ?? '').trim()
    const created = await createExportLink(calendarId, label || null)
    links[calendarId]?.links.push(created)
    labels[calendarId] = ''
  } catch (e) {
    reportError(e, 'settings.export.err.create')
    toast.error(e, t('settings.export.err.create'))
  } finally {
    busy[calendarId] = false
  }
}

async function revokeLink(calendarId: number, linkId: string): Promise<void> {
  if (!(await confirmDialog(t('settings.export.revokeOneConfirm')))) return
  busy[calendarId] = true
  try {
    await revokeExportLink(calendarId, linkId)
    const entry = links[calendarId]
    if (entry) entry.links = entry.links.filter((link) => link.id !== linkId)
  } catch (e) {
    reportError(e, 'settings.export.err.revokeOne')
    toast.error(e, t('settings.export.err.revokeOne'))
  } finally {
    busy[calendarId] = false
  }
}

async function revoke(): Promise<void> {
  if (!(await confirmDialog(t('settings.export.revokeConfirm')))) return
  revoking.value = true
  try {
    emit('revoked', await revokeExportLinks())
    for (const id of Object.keys(links)) delete links[Number(id)]
    await loadLinks()
  } catch (e) {
    reportError(e, 'settings.export.err.revoke')
    toast.error(e, t('settings.export.err.revoke'))
  } finally {
    revoking.value = false
  }
}

onMounted(() => void loadLinks())
</script>

<template>
  <section class="card">
    <h3>{{ t('settings.export.title') }}</h3>
    <p class="hint">{{ t('settings.export.intro') }}</p>

    <p v-if="store.calendarsLoaded && !store.calendars.length" class="hint">{{ t('settings.export.none') }}</p>
    <ul class="lists">
      <li v-for="calendar in store.calendars" :key="calendar.id">
        <div class="row">
          <span class="name">
            {{ calendar.name }} <span class="hint">· {{ calendar.account_name }}</span>
          </span>
          <button type="button" class="ui-btn" :disabled="!links[calendar.id]" @click="download(calendar.id)">
            {{ t('settings.export.download') }}
          </button>
        </div>
        <template v-if="links[calendar.id]">
          <div v-for="link in links[calendar.id].links" :key="link.id" class="link">
            <div class="row">
              <span class="name">{{ link.label || t('settings.export.unnamed') }}</span>
              <button type="button" class="ui-btn" @click="copy(link.url)">{{ t('common.copy') }}</button>
              <button
                type="button"
                class="ui-btn ui-btn-danger"
                :disabled="busy[calendar.id]"
                @click="revokeLink(calendar.id, link.id)"
              >
                {{ t('settings.export.revokeOne') }}
              </button>
            </div>
            <input
              class="ui-input"
              readonly
              :value="link.url"
              :aria-label="t('settings.export.subscribeLabel')"
              @focus="($event.target as HTMLInputElement).select()"
            />
            <p class="hint">
              {{ t('settings.export.created', { date: formatDate(link.created_at) }) }} ·
              {{
                link.last_used_at
                  ? t('settings.export.lastUsed', { date: formatDate(link.last_used_at) })
                  : t('settings.export.neverUsed')
              }}
            </p>
          </div>
          <form class="row" @submit.prevent="addLink(calendar.id)">
            <input
              v-model="labels[calendar.id]"
              class="ui-input"
              maxlength="128"
              :placeholder="t('settings.export.labelPlaceholder')"
              :aria-label="t('settings.export.labelPlaceholder')"
            />
            <button type="submit" class="ui-btn" :disabled="busy[calendar.id]">{{ t('settings.export.newLink') }}</button>
          </form>
        </template>
      </li>
    </ul>

    <p class="hint">{{ t('settings.export.hint') }}</p>
    <p v-if="validSinceText" class="hint">{{ t('settings.export.validSince', { date: validSinceText }) }}</p>
    <button type="button" class="ui-btn ui-btn-danger" :disabled="revoking" @click="revoke">
      {{ t('settings.export.revoke') }}
    </button>
  </section>
</template>

<style scoped>
.card {
  background: var(--tg-section-bg-color);
  border-radius: 0.75rem;
  padding: 1rem;
  margin-bottom: 1rem;
}

.hint {
  font-size: 0.85rem;
  color: var(--tg-hint-color);
}

.lists {
  list-style: none;
  margin: 0 0 0.75rem;
  padding: 0;
  display: grid;
  gap: 0.6rem;
}

.row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.row + .row {
  margin-top: 0.4rem;
}

.name {
  flex: 1 1 10rem;
  min-width: 0;
  overflow-wrap: anywhere;
}

.row input {
  flex: 1 1 12rem;
  min-width: 0;
}

.link {
  display: grid;
  gap: 0.3rem;
  margin-top: 0.6rem;
  padding-left: 0.75rem;
  border-left: 2px solid var(--tg-hint-color);
}

.link .hint {
  margin: 0;
}

.link input {
  width: 100%;
  box-sizing: border-box;
}
</style>
